import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppSettingsService } from '@app/core/services/app-settings.service';
import type { FileItem, FilesUploadNameSyntax } from '@app/core/services/file.service';
import { firstValueFrom, from, Observable, switchMap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class FileServerService {
    constructor(
        private http: HttpClient,
        private appSettings: AppSettingsService,
    ) {}

    public getBlob(url: string): Observable<Blob> {
        return this.http
            .get(url, { observe: 'response', responseType: 'blob' })
            .pipe(switchMap((response) => from(this.ensureContentType(response.body ?? new Blob(), response.headers.get('Content-Type'), response.headers.get('Content-Disposition'), url))));
    }

    public async uploadFile(params: UploadFileParams): Promise<FileItem> {
        const url = this.getUploadUrl(params.url || this.appSettings.settings.fileServerUrl);
        const fileUpload = new FormData();

        fileUpload.append('fileUploadObj[replace]', String(params.replace ?? false));
        fileUpload.append('fileUploadObj[maxWidth]', String(params.maxWidth ?? 800));
        fileUpload.append('fileUploadObj[maxHeight]', String(params.maxHeight ?? 400));
        fileUpload.append('fileUploadObj[fileNameTemplate]', params.fileNameSyntax?.filenametemplate ?? '');
        fileUpload.append('fileUploadObj[localFileNameTemplate]', params.fileNameSyntax?.localfilenametemplate ?? '');
        fileUpload.append('fileUploadObj[position]', '');
        fileUpload.append('file', params.file, params.file.name);

        const response = await firstValueFrom(this.http.post<FileItem | FileUploadResponse>(url, fileUpload));
        const uploadedFile = 'data' in response ? response.data : response;

        if (response.isError) throw Error(response.message || 'File upload failed');

        return uploadedFile;
    }

    private getUploadUrl(fileServerUrl: string): string {
        if (!fileServerUrl) throw Error('Invalid file server URL');
        return `${fileServerUrl.replace(/\/+$/, '')}/api/file/upload`;
    }

    private async ensureContentType(blob: Blob, responseContentType: string | null, contentDisposition: string | null, url: string): Promise<Blob> {
        const contentType = this.getUsableContentType(blob.type) || this.getUsableContentType(responseContentType) || this.getContentTypeFromName(`${contentDisposition ?? ''} ${url}`) || (await this.detectImageContentType(blob));

        return contentType && contentType !== blob.type ? blob.slice(0, blob.size, contentType) : blob;
    }

    private getUsableContentType(contentType: string | null): string {
        const normalized = contentType?.split(';')[0].trim().toLowerCase() ?? '';
        return normalized && normalized !== 'application/octet-stream' ? normalized : '';
    }

    private getContentTypeFromName(value: string): string {
        let decodedValue = value.toLowerCase();

        try {
            decodedValue = decodeURIComponent(decodedValue);
        } catch {
            // The original value can still contain a usable filename extension.
        }

        const extension = decodedValue.match(/\.([a-z0-9]+)(?:$|[?&#"';\s])/i)?.[1];

        return extension ? IMAGE_CONTENT_TYPES[extension] ?? '' : '';
    }

    private async detectImageContentType(blob: Blob): Promise<string> {
        const bytes = new Uint8Array(await blob.slice(0, 16).arrayBuffer());

        if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
        if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'image/png';
        if (this.bytesToText(bytes, 0, 3) === 'GIF') return 'image/gif';
        if (this.bytesToText(bytes, 0, 4) === 'RIFF' && this.bytesToText(bytes, 8, 12) === 'WEBP') return 'image/webp';
        if (bytes[0] === 0x42 && bytes[1] === 0x4d) return 'image/bmp';
        if ((bytes[0] === 0x49 && bytes[1] === 0x49 && bytes[2] === 0x2a && bytes[3] === 0x00) || (bytes[0] === 0x4d && bytes[1] === 0x4d && bytes[2] === 0x00 && bytes[3] === 0x2a)) return 'image/tiff';

        if (this.bytesToText(bytes, 4, 8) === 'ftyp') {
            const brand = this.bytesToText(bytes, 8, 12);
            if (['avif', 'avis'].includes(brand)) return 'image/avif';
            if (['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1'].includes(brand)) return 'image/heic';
        }

        const text = await blob.slice(0, 256).text();
        return /<(?:\?xml[^>]*>\s*)?svg(?:\s|>)/i.test(text) ? 'image/svg+xml' : '';
    }

    private bytesToText(bytes: Uint8Array, start: number, end: number): string {
        return String.fromCharCode(...bytes.slice(start, end));
    }
}

const IMAGE_CONTENT_TYPES: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    tif: 'image/tiff',
    tiff: 'image/tiff',
    svg: 'image/svg+xml',
    heic: 'image/heic',
    heif: 'image/heic',
    avif: 'image/avif',
};

export interface UploadFileParams {
    file: File;
    url?: string;
    replace?: boolean;
    maxWidth?: number;
    maxHeight?: number;
    fileNameSyntax?: Partial<FilesUploadNameSyntax>;
}

interface FileUploadResponse {
    data: FileItem;
    isError?: boolean;
    message?: string;
}
